import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchCourses, fetchCourseDetails, fetchCourseHistory } from '../services/canvasService';
import { MOCK_COURSE_HISTORY } from '../data/mockData';
import {
  COURSES_STALE_TIME_MS,
  COURSES_GC_TIME_MS,
  COURSE_DETAILS_STALE_TIME_MS,
  COURSE_DETAILS_GC_TIME_MS,
  COURSE_HISTORY_STALE_TIME_MS,
  COURSE_HISTORY_GC_TIME_MS,
} from '../contexts/canvasQueryConfig';

// Task 4.5: the course-fetching/query-boundary half of the pre-4.5
// CanvasDataContext.tsx (tasks 4.4a-4.4c), extracted into a plain hook so
// CanvasDataContext.tsx itself can stay under its line budget as a thin
// composition layer. Not a context -- just the read/fetch logic, called
// once from CanvasDataContext's provider component. See useUserSwitching.ts
// for the sibling hook that owns user-switching orchestration.

const EMPTY_COURSES: any[] = [];

interface UseCourseDataArgs {
  pendingUserSelection: boolean;
  coursesMode: 'demo' | 'guest' | 'auth' | 'offline';
  userId: string | null;
  getActiveCredentials: () => any;
  demoCourses: any[];
  demoCourseDetails: Record<string | number, any>;
  offlineCourses?: any[];
  offlineCourseDetails?: Record<string | number, any>;
}

export function useCourseData({
  pendingUserSelection,
  coursesMode,
  userId,
  getActiveCredentials,
  demoCourses,
  demoCourseDetails,
  offlineCourses,
  offlineCourseDetails,
}: UseCourseDataArgs) {
  const queryClient = useQueryClient();
  const isDemoMode = coursesMode === 'demo';
  const isOfflineMode = coursesMode === 'offline';

  // Task 6.0e. Demo and offline differ in *what* the data means -- fabricated
  // sample data versus the user's own captured data -- but behave identically
  // here: both supply courses up front and must never issue a Canvas request.
  // Every guard below that used to read "is this demo mode" actually meant
  // "does this mode already have its data", which is this.
  const usesStaticData = isDemoMode || isOfflineMode;

  // Real (non-demo) per-course-history and per-course-details caches, keyed
  // by courseId. Demo mode reads demoCourseDetails/MOCK_COURSE_HISTORY
  // instead (see `courseDetails` and getCourseHistory below).
  const historyCache = useRef<Record<string | number, any>>({});
  const [realCourseDetails, setRealCourseDetails] = useState<Record<string | number, any>>({});

  const [loadingCourses, setLoadingCourses] = useState(false);
  const [loadingCourseDetails, setLoadingCourseDetails] = useState(false);
  const [loadingCourseHistory, setLoadingCourseHistory] = useState(false);
  const [error, setError] = useState('');

  // Drop cached per-course reads whenever the active identity (mode +
  // userId) changes. realCourseDetails/historyCache are flat courseId-keyed
  // caches, NOT scoped by identity the way the TanStack query keys below
  // are -- without this, switching users could show the previous user's
  // cached course details for a colliding courseId.
  useEffect(() => {
    setRealCourseDetails({});
    historyCache.current = {};
  }, [coursesMode, userId]);

  // Which credential source is producing "courses" right now. demo/guest/
  // authenticated return different data for the same logical resource, so
  // the cache key must encode this alongside userId -- a key of just
  // ['courses'] would leak one identity's courses into another's cache
  // entry on a mode or user switch.
  const coursesQueryKey = useMemo(() => ['courses', coursesMode, userId], [coursesMode, userId]);
  const coursesQueryEnabled = !usesStaticData && !pendingUserSelection && !!getActiveCredentials();

  // Task 4.4b: course details/history are fetched on demand (keyed by
  // courseId, called imperatively) rather than mounted as a useQuery, so
  // they're routed through the query client's `fetchQuery` instead -- same
  // cache, same credential-identity keying as coursesQueryKey.
  const courseDetailsQueryKey = useCallback(
    (courseId: string | number, credUserId: string) => ['courseDetails', coursesMode, credUserId, courseId],
    [coursesMode]
  );
  const courseHistoryQueryKey = useCallback(
    (courseId: string | number, credUserId: string) => ['courseHistory', coursesMode, credUserId, courseId],
    [coursesMode]
  );

  // Courses list, migrated to TanStack Query (task 4.4a). Demo mode is
  // excluded via `enabled` -- it never hits the network; its data comes
  // from demoCourses via the `courses` derivation below.
  const {
    data: fetchedCourses,
    isFetching: isFetchingCourses,
    error: coursesQueryError,
    refetch: refetchCoursesQuery,
  } = useQuery<any[]>({
    queryKey: coursesQueryKey,
    queryFn: async () => {
      const credentials = getActiveCredentials();
      if (!credentials) {
        return EMPTY_COURSES;
      }
      return fetchCourses(credentials.token, credentials.userId, credentials.rootUrl);
    },
    enabled: coursesQueryEnabled,
    staleTime: COURSES_STALE_TIME_MS,
    gcTime: COURSES_GC_TIME_MS,
  });

  useEffect(() => {
    if (coursesQueryError) {
      console.error('Error loading courses:', coursesQueryError);
      setError('Failed to load courses. Please check your Canvas API credentials.');
    }
  }, [coursesQueryError]);

  // Task 4.5: dropped the old overrideUserId parameter (a user-switch's
  // "avoid a one-render-late flash" path) -- the reactive query-key path
  // above is already correct on its own once the active identity's context
  // value updates, so the override was a UX nicety, not a correctness
  // requirement.
  const loadCourses = useCallback(async () => {
    if (usesStaticData) {
      console.log('Static data mode active: skipping real course API call');
      return;
    }
    if (pendingUserSelection) {
      return;
    }

    const credentials = getActiveCredentials();
    if (!credentials) {
      setError('Canvas API credentials not found.');
      return;
    }

    setLoadingCourses(true);
    setError('');
    try {
      await refetchCoursesQuery();
    } catch (err) {
      console.error('Error loading courses:', err);
      setError('Failed to load courses. Please check your Canvas API credentials.');
    } finally {
      setLoadingCourses(false);
    }
  }, [usesStaticData, pendingUserSelection, getActiveCredentials, refetchCoursesQuery]);

  const courses = isDemoMode
    ? demoCourses
    : isOfflineMode
      ? (offlineCourses ?? EMPTY_COURSES)
      : (pendingUserSelection ? EMPTY_COURSES : (fetchedCourses ?? EMPTY_COURSES));

  const courseDetails = useMemo(() => (
    isDemoMode
      ? demoCourseDetails
      : isOfflineMode
        ? (offlineCourseDetails ?? {})
        : realCourseDetails
  ), [isDemoMode, isOfflineMode, demoCourseDetails, offlineCourseDetails, realCourseDetails]);

  const loadAllCourseDetails = useCallback(async (activeOnly = false) => {
    if (usesStaticData) {
      console.log('Static data mode active: course details already loaded');
      return;
    }
    if (!courses || courses.length === 0) return;

    const credentials = getActiveCredentials();
    if (!credentials) {
      setError('Canvas API credentials not found');
      return;
    }

    setLoadingCourseDetails(true);
    setError('');

    try {
      if (!Array.isArray(courses)) {
        console.warn('Courses data is not an array:', courses);
        setLoadingCourseDetails(false);
        return;
      }

      const coursesToProcess = activeOnly
        ? courses.filter(course => !course.concluded && !course.access_restricted)
        : courses;
      const courseIdsToLoad = coursesToProcess.map(course => course.api_course_id);
      const alreadyLoadedCourseIds = Object.keys(realCourseDetails).map(id => parseInt(id));
      const coursesToLoad = courseIdsToLoad.filter(id => !alreadyLoadedCourseIds.includes(id));

      if (coursesToLoad.length === 0) {
        setLoadingCourseDetails(false);
        return;
      }

      // Routed through the query client so this shares a cache entry with
      // getCourseDetails below (a course opened individually first, then
      // swept up by this bulk loader -- or vice versa -- hits the network
      // once, not twice).
      const detailPromises = coursesToLoad.map(courseId =>
        queryClient.fetchQuery({
          queryKey: courseDetailsQueryKey(courseId, credentials.userId),
          queryFn: () => fetchCourseDetails(credentials.token, credentials.userId, credentials.rootUrl, courseId),
          staleTime: COURSE_DETAILS_STALE_TIME_MS,
          gcTime: COURSE_DETAILS_GC_TIME_MS,
        }).catch(err => {
          console.error(`Error loading details for course ${courseId}:`, err);
          return { id: courseId, error: true };
        })
      );

      const results = await Promise.all(detailPromises);
      setRealCourseDetails(prevDetails => {
        const newDetails = { ...prevDetails };
        results.forEach(result => {
          if (!result.error) {
            newDetails[result.id] = result;
          }
        });
        return newDetails;
      });
    } catch (err) {
      console.error('Error loading course details:', err);
      setError('Failed to load course details. Please try again later.');
    } finally {
      setLoadingCourseDetails(false);
    }
  }, [courses, realCourseDetails, getActiveCredentials, usesStaticData, queryClient, courseDetailsQueryKey]);

  // Resets this hook's own per-course caches (not the TanStack cache, which
  // is already identity-scoped by the query keys above). Exposed so
  // useUserSwitching can clear it immediately on a user switch, on top of
  // the reactive effect above.
  const resetCourseData = useCallback(() => {
    setRealCourseDetails({});
    historyCache.current = {};
  }, []);

  // `string | number` throughout, matching what the query-key builders above
  // already declare for their courseId segment and what the courseId-keyed
  // caches (realCourseDetails, historyCache) are typed as. Annotating these
  // does not change any key's shape or the type of any key segment.
  const getCourseDetails = useCallback(async (courseId: string | number) => {
    // Task 6.0q. The static-data guard 6.0e gave loadCourses,
    // loadAllCourseDetails and getCourseHistory, missing here until now.
    // Demo and offline have no credentials by design, so without this the
    // credentials check below reports "Canvas API credentials not found" for
    // data the `courseDetails` map is already holding. Returning null rather
    // than falling through is deliberate: a course absent from a snapshot is
    // genuinely absent, and fetching it is what these modes forbid.
    if (usesStaticData) {
      return courseDetails[courseId] ?? null;
    }

    const credentials = getActiveCredentials();
    if (!credentials) {
      setError('Canvas API credentials not found');
      return null;
    }
    if (realCourseDetails[courseId]) {
      return realCourseDetails[courseId];
    }

    setLoadingCourseDetails(true);
    setError('');
    try {
      // Task 4.4b: routed through the query client so this resource is
      // cached and invalidated the same way the courses list is -- keyed
      // by credential identity (coursesMode) + userId + courseId.
      const courseData = await queryClient.fetchQuery({
        queryKey: courseDetailsQueryKey(courseId, credentials.userId),
        queryFn: () => fetchCourseDetails(credentials.token, credentials.userId, credentials.rootUrl, courseId),
        staleTime: COURSE_DETAILS_STALE_TIME_MS,
        gcTime: COURSE_DETAILS_GC_TIME_MS,
      });
      setRealCourseDetails(prev => ({ ...prev, [courseId]: courseData }));
      return courseData;
    } catch (err) {
      console.error(`Error loading details for course ${courseId}:`, err);
      setError(`Failed to load course details for course ${courseId}`);
      return null;
    } finally {
      setLoadingCourseDetails(false);
    }
  }, [usesStaticData, courseDetails, getActiveCredentials, realCourseDetails, queryClient, courseDetailsQueryKey]);

  const getCourse = useCallback(async (courseId: string | number) => {
    // `String(courseId)` is not a behaviour change: parseInt is specified to
    // convert its argument with ToString first, so parseInt(5) and
    // parseInt('5') were already the same call. It only makes the existing
    // conversion visible to the type checker.
    const existingCourse = courses.find(c => c.id === parseInt(String(courseId)) || c.id === courseId);
    if (existingCourse) {
      return existingCourse;
    }

    // Task 6.0q, same hole as getCourseDetails above. This one is not
    // currently reachable -- the lookup above matches for every course a
    // snapshot or the mock fixture carries -- but leaving one of two
    // identical holes open is arbitrary, and the next caller to miss the
    // list would get the credentials error for a mode that has no
    // credentials to give.
    if (usesStaticData) {
      return null;
    }

    const credentials = getActiveCredentials();
    if (!credentials) {
      setError('Canvas API credentials not found');
      return null;
    }

    setLoadingCourses(true);
    setError('');
    try {
      const courseData = await fetchCourses(credentials.token, credentials.userId, credentials.rootUrl, courseId);
      return courseData[0] || null;
    } catch (err) {
      console.error(`Error loading course ${courseId}:`, err);
      setError(`Failed to load course ${courseId}`);
      return null;
    } finally {
      setLoadingCourses(false);
    }
  }, [courses, usesStaticData, getActiveCredentials]);

  const getCourseHistory = useCallback(async (courseId: string | number) => {
    if (historyCache.current[courseId]) {
      return historyCache.current[courseId];
    }
    // Real consumers check the courseDetails dict (demoCourseDetails in
    // demo mode) before calling this; this is the fallback if they don't.
    if (isOfflineMode) {
      // Task 6.0ah. This request does not go to Canvas: it is the app's own
      // GET /courses/{id}/history, which the backend builds from the user's
      // stored snapshots (one point per snapshot) and which needs only the
      // Canvas user id, no token. So it is safe here, where the live branch
      // below is not (offline has no credentials). Empty strings make
      // buildHeaders send X-Canvas-User-Id alone.
      if (!userId) {
        return [];
      }
      try {
        const offlineHistory = await queryClient.fetchQuery({
          queryKey: courseHistoryQueryKey(courseId, userId),
          queryFn: () => fetchCourseHistory('', userId, '', courseId),
          staleTime: COURSE_HISTORY_STALE_TIME_MS,
          gcTime: COURSE_HISTORY_GC_TIME_MS,
        });
        historyCache.current[courseId] = offlineHistory;
        return offlineHistory;
      } catch (err) {
        // One card's history failing is not a page-level error: log it and
        // let the page show its empty state.
        console.error(`Error loading stored history for course ${courseId}:`, err);
        return [];
      }
    }

    if (isDemoMode) {
      console.log('Demo mode: no cached history for course', courseId);
      // MOCK_COURSE_HISTORY is a fixed six-key object literal in mockData.js,
      // so an arbitrary courseId is NOT guaranteed to be a key. The `|
      // undefined` in this view is the load-bearing part: widening it to
      // `Record<string, Point[]>` would type-check just as well while
      // claiming every lookup hits, quietly making the `|| []` below look
      // dead. It is not dead -- it is the demo-mode fallback for any course
      // outside the six.
      const mockHistory = MOCK_COURSE_HISTORY as Record<string, { date: string; score: number }[] | undefined>;
      return mockHistory[courseId] || [];
    }

    const credentials = getActiveCredentials();
    if (!credentials) {
      setError('Canvas API credentials not found');
      return [];
    }

    setLoadingCourseHistory(true);
    setError('');
    try {
      // Task 4.4b: routed through the query client, same credential-identity
      // keying as getCourseDetails above.
      const historyData = await queryClient.fetchQuery({
        queryKey: courseHistoryQueryKey(courseId, credentials.userId),
        queryFn: () => fetchCourseHistory(credentials.token, credentials.userId, credentials.rootUrl, courseId),
        staleTime: COURSE_HISTORY_STALE_TIME_MS,
        gcTime: COURSE_HISTORY_GC_TIME_MS,
      });
      historyCache.current[courseId] = historyData;
      return historyData;
    } catch (err) {
      console.error(`Error loading history for course ${courseId}:`, err);
      setError(`Failed to load grade history for course ${courseId}`);
      return [];
    } finally {
      setLoadingCourseHistory(false);
    }
  }, [getActiveCredentials, isDemoMode, isOfflineMode, userId, queryClient, courseHistoryQueryKey]);

  // Task 4.4b: previously delegated to the old tag-based API cache layer
  // (since removed) via clearUserCache; now that TanStack Query is the only
  // read cache, clear it directly. Removing (rather than invalidating) each
  // resource's prefix drops every courseId-keyed entry for this credential
  // identity in one call, mirroring the old `clearCacheByTag('user_X')`.
  const clearCache = useCallback(() => {
    try {
      const credentials = getActiveCredentials();
      if (credentials?.userId) {
        queryClient.removeQueries({ queryKey: ['courses', coursesMode, credentials.userId] });
        queryClient.removeQueries({ queryKey: ['courseDetails', coursesMode, credentials.userId] });
        queryClient.removeQueries({ queryKey: ['courseHistory', coursesMode, credentials.userId] });
      }
    } catch (error) {
      console.error('Failed to clear cache:', error);
    }
  }, [getActiveCredentials, queryClient, coursesMode]);

  return {
    courses,
    courseDetails,
    loadingCourses,
    loadingCourseDetails,
    loadingCourseHistory,
    isFetchingCourses,
    error,
    setError,
    setLoadingCourses,
    setLoadingCourseDetails,
    setLoadingCourseHistory,
    loadCourses,
    loadAllCourseDetails,
    resetCourseData,
    getCourse,
    getCourseDetails,
    getCourseHistory,
    clearCache,
  };
}
